// Локальная PowerSync schema — описание таблиц, которые держим в SQLite на
// клиенте.
//
// ИСТОЧНИК ИСТИНЫ — РЕАЛЬНЫЕ колонки Postgres (`information_schema.columns`),
// которые PowerSync синкает через `infra/powersync/sync-rules.yaml`. Все stream
// queries используют `SELECT *` без алиасов, поэтому клиент получает РЕАЛЬНЫЕ
// имена колонок БД. Любое расхождение имени → PowerSync маппит null → краш UI
// (`Cannot read properties of null`). Поэтому каждая `Table` ниже описывает
// колонки 1:1 с серверной Drizzle schema (`apps/server/src/shared/db/schema/`).
//
// Преобразование UI-DTO (camelCase, стабильная форма для компонентов) делается
// в адаптерах `apps/web/src/components/*/_api.ts` и хуках
// `apps/web/src/lib/powersync/queries/*` — НЕ здесь.
//
// Тип-маппинг Postgres → PowerSync column:
//   text / varchar / USER-DEFINED(enum) / timestamptz / date  → column.text
//   integer / bigint / smallint                               → column.integer
//   real / numeric / double precision                         → column.real
//   boolean                                                   → column.integer (SQLite 0/1)
//   jsonb / ARRAY                                              → column.text (PowerSync сериализует JSON-строкой)
//
// При добавлении поля в Drizzle schema:
//   1. Drizzle migrate (expand-only — rules/database.md)
//   2. Если поле нужно клиенту — добавить здесь с верным типом
//   3. `docker compose restart powersync` → клиент при mismatch делает локальный
//      wipe + re-sync (Schema-версионирование через `Schema` экспорт)
//
// Phase 0 переименования таблиц (актуальные имена БД):
//   study_sets → folders → folders (переименование 2026-08-14)
//   recordings        → lecture_sessions
//   quizzes (таблица) → tests
//   quiz_attempts     → attempt_records + attempt_drafts
//   tutor_chats       → tutor_sessions
//   essays            → essay_submissions
//   audio_summaries   → audio_recaps
//   daily_activity    → user_activity_log
//   streaks           → streak_state
import { column, Schema, Table } from "@powersync/web";

// id — implicit PK в PowerSync (column.text), не объявляем явно.

// ── users (sync-rules: user_profile) ──────────────────────────────────────
const users = new Table({
	email: column.text,
	sso_exchange_id: column.text,
	full_name: column.text,
	avatar_url: column.text,
	language: column.text,
	answer_locale: column.text,
	theme: column.text,
	subscription_tier: column.text, // enum — ПРОИЗВОДНЫЙ: max(локальный, права IEC ID)
	local_subscription_tier: column.text, // enum — локальная подписка (наш биллинг)
	status: column.text, // enum
	country: column.text,
	preferred_education_level: column.text, // enum
	preferred_prior_knowledge: column.text, // enum
	education_country: column.text,
	education_subsystem: column.text,
	age_group: column.text, // enum
	age_confirmed_at: column.text,
	birth_year: column.integer,
	deletion_requested_at: column.text,
	deleted_at: column.text,
	srs_reminder_hour: column.integer,
	srs_reminder_enabled: column.integer, // bool
	timezone: column.text,
	marketing_opt_in: column.integer, // bool
	accepted_terms_at: column.text,
	analytics_opt_out: column.integer, // bool — cookie-категория analytics (ОТКАЗ)
	consent_cookie_preferences: column.integer, // bool — cookie-категория preferences (РАЗРЕШЕНИЕ)
	// Зеркало IEC ID (миграция 0146). users синкается `SELECT *`, поэтому колонки
	// приезжают сами — но без объявления здесь PowerSync их молча отбрасывает.
	// Баланс NULL = «не знаем», НЕ ноль: блок Баллов при NULL скрывается.
	iec_points_balance: column.integer,
	iec_points_synced_at: column.text,
	iec_plan: column.text, // effectiveTier экосистемы — для бейджа IEC+, не для доступа
	iec_legal_pending: column.integer, // bool
	iec_linked: column.integer, // bool
	referral_source: column.text,
	last_seen_at: column.text,
	created_at: column.text,
	updated_at: column.text,
	notification_preferences: column.text, // jsonb
	read_only_until: column.text,
	read_only_reason: column.text,
	notes_auto_generate: column.integer, // bool
	notes_default_detail_level: column.text,
	auto_generate_on_upload: column.integer, // bool — мастер-тумблер автогенерации (0055)
	// Обучающая система (миграция 0083): настройки чтения и офлайн-аудио.
	glossary_highlight_enabled: column.integer, // bool
	glossary_highlight_course_only: column.integer, // bool
	audio_offline_prefetch: column.integer, // bool
	audio_offline_wifi_only: column.integer, // bool
	name: column.text,
	image: column.text,
	email_verified: column.integer, // bool
	role: column.text, // enum
});

// ── user_stats (sync-rules: user_profile) ─────────────────────────────────
// Server-aggregated counters (1:1 c users). Read-only клиенту: нет writeHandler,
// все обновления — server-side (mutation post-hooks / cron). PK = user_id (FK к users).
const user_stats = new Table({
	user_id: column.text,
	materials_count: column.integer,
	flashcards_count: column.integer,
	tests_count: column.integer,
	completed_quizzes: column.integer,
	current_streak: column.integer,
	longest_streak: column.integer,
	total_study_minutes: column.integer,
	total_ai_spend_minor: column.real, // DECIMAL(15,2) → real (SQLite NUMERIC affinity)
	last_activity_at: column.text,
});

// ── quotas (sync-rules: user_quotas) ──────────────────────────────────────
const quotas = new Table({
	user_id: column.text,
	custom_limits: column.text, // jsonb
	expires_at: column.text,
	set_by: column.text,
	created_at: column.text,
	updated_at: column.text,
	reason: column.text,
	set_by_admin_id: column.text,
});

// ── notifications (sync-rules: user_notifications) ────────────────────────
const notifications = new Table(
	{
		user_id: column.text,
		type: column.text, // enum
		title: column.text,
		message: column.text,
		action_url: column.text,
		icon_type: column.text,
		icon_color: column.text,
		status: column.text, // enum
		group_key: column.text,
		created_at: column.text,
		read_at: column.text,
		expires_at: column.text,
		data: column.text, // jsonb
		delivered_at: column.text,
	},
	{ indexes: { by_user: ["user_id", "created_at"] } },
);

// ── notification_preferences (sync-rules: user_notifications) ────────────
// PK = user_id (одна строка на пользователя). Используется UI настроек.
const notification_preferences = new Table({
	user_id: column.text,
	web_push_enabled: column.integer, // bool
	mobile_push_enabled: column.integer, // bool
	email_enabled: column.integer, // bool
	in_app_enabled: column.integer, // bool
	ai_task_complete_enabled: column.integer, // bool
	srs_reminder_enabled: column.integer, // bool
	streak_nudge_enabled: column.integer, // bool
	quota_warning_enabled: column.integer, // bool
	subscription_alert_enabled: column.integer, // bool
	security_alert_enabled: column.integer, // bool
	product_updates_enabled: column.integer, // bool
	marketing_enabled: column.integer, // bool
	// Ключ `recommendations` из consent-revoked IEC ID (миграция 0146).
	recommendations_enabled: column.integer, // bool
	weekly_digest_enabled: column.integer, // bool
	quiet_hours_enabled: column.integer, // bool
	quiet_hours_start: column.text,
	quiet_hours_end: column.text,
	updated_at: column.text,
});

// ── folders (sync-rules: user_materials) ──────────────────────────────────
const folders = new Table(
	{
		user_id: column.text,
		name: column.text,
		description: column.text,
		color: column.text,
		is_public: column.integer, // bool
		share_token: column.text,
		share_link_revoked_at: column.text,
		sort_order: column.integer,
		favorite: column.integer, // bool
		// Обучающая система (миграция 0083): 'folder' | 'course_source'.
		kind: column.text,
		parent_id: column.text,
		materials_count: column.integer,
		generated_flashcards: column.integer,
		generated_tests: column.integer,
		created_at: column.text,
		updated_at: column.text,
		deleted_at: column.text,
	},
	{ indexes: { by_user: ["user_id", "updated_at"], by_kind: ["user_id", "kind"] } },
);

// ── set_items (sync-rules: user_materials) ────────────────────────────────
// Collections (миграция 0060) — единый полиморфный pivot коллекций.
// item_type — enum set_item_type (material/note/flashcard_set/test/...).
// item_id — БЕЗ FK (полиморфизм); целостность на приложении (writeHandler).
const set_items = new Table(
	{
		folder_id: column.text,
		item_type: column.text, // enum set_item_type
		item_id: column.text,
		sort_order: column.integer,
		added_at: column.text,
	},
	{ indexes: { by_set: ["folder_id", "sort_order"], by_item: ["item_type", "item_id"] } },
);

// ── materials (sync-rules: user_materials) ────────────────────────────────
const materials = new Table(
	{
		user_id: column.text,
		name: column.text,
		type: column.text, // enum material_type (pdf/doc/text/note/image/audio/video/youtube/...)
		mime_type: column.text,
		file_size_bytes: column.integer, // bigint
		original_file: column.text,
		content: column.text,
		transcription: column.text,
		notes: column.text,
		duration: column.integer,
		favorite: column.integer, // bool
		tags: column.text, // ARRAY → JSON-строка
		view_count: column.integer,
		youtube_thumbnail: column.text,
		processing_status: column.text, // enum
		error_message: column.text,
		error_code: column.text,
		error_retry_count: column.integer,
		share_token: column.text,
		share_link_revoked_at: column.text,
		thumb_hash_base64: column.text,
		thumbnail_url: column.text,
		content_hash_sha256: column.text,
		ocr_used: column.integer, // bool
		detected_language: column.text,
		language: column.text,
		extraction_ai_model: column.text,
		extraction_prompt_version: column.text,
		extraction_trace_id: column.text,
		transcription_ai_model: column.text,
		transcription_prompt_version: column.text,
		transcription_trace_id: column.text,
		// source_kind — NULL для обычных материалов; 'tutor_chat' = derived-материал
		// диалога («Создать на основе чата»). Скрывается из browse-списка/пикеров,
		// остаётся доступен по id для navigate-целей (/tutor/me, /audio-recaps/new).
		source_kind: column.text,
		// Каталог курсов (миграция 0083): 'user' | 'system' и 'open' | 'protected'.
		// pending_course_id СОЗНАТЕЛЬНО отсутствует (server-only, как pending_set_id).
		owner_kind: column.text,
		access: column.text,
		// Discover sources (миграция 0099): 'upload' | 'web'. Панель источников
		// по ним рисует фавикон, домен и «обновлено N дн. назад» у веб-строк —
		// поэтому колонки синкаются, а не добираются отдельным запросом.
		origin: column.text,
		source_url: column.text,
		fetched_at: column.text,
		// etag / last_modified / web_content_hash — server-only: нужны условному
		// GET при перечитывании, клиенту показывать нечего.
		created_at: column.text,
		updated_at: column.text,
		deleted_at: column.text,
	},
	{
		indexes: {
			by_user: ["user_id", "updated_at"],
			by_status: ["processing_status"],
		},
	},
);

// ── source_discovery_runs / _candidates (sync-rules: user_source_discovery) ─
// Discover sources (миграция 0099). READ-ONLY: writeHandler'а нет намеренно —
// прогон создаёт tRPC `discovery.search`, наполняет Inngest, удаляет
// `discovery.deleteRun`. Панель читает обе таблицы локально, чтобы статусы
// импорта («ищем» → «готово» → «за пейволом») обновлялись без поллинга.
const source_discovery_runs = new Table(
	{
		user_id: column.text,
		course_id: column.text,
		query: column.text,
		mode: column.text, // 'fast' | 'deep'
		// pending | planning | searching | ranking | ready | failed
		// + Deep (0100): reading | reflecting | synthesizing | importing
		status: column.text,
		locale: column.text,
		search_queries: column.text, // jsonb → JSON-строка
		search_entry_points: column.text, // jsonb → JSON-строка (чипы Google)
		// Материал-отчёт Deep Research (0100). Читается панелью для CTA
		// «Открыть отчёт»: без колонки в клиентской схеме строка приезжает,
		// а поле молча теряется на границе SQLite.
		report_material_id: column.text,
		error_code: column.text,
		ai_model: column.text,
		prompt_version: column.text,
		generation_trace_id: column.text,
		created_at: column.text,
		updated_at: column.text,
		deleted_at: column.text,
	},
	{ indexes: { by_course: ["course_id", "created_at"] } },
);

const source_discovery_candidates = new Table(
	{
		run_id: column.text,
		user_id: column.text,
		url: column.text,
		title: column.text,
		description: column.text,
		site_name: column.text,
		favicon_s3_key: column.text,
		source_type: column.text,
		published_at: column.text,
		word_count: column.integer,
		lang: column.text,
		score: column.real,
		rank: column.integer,
		status: column.text, // candidate | importing | imported | failed
		error_code: column.text,
		material_id: column.text,
		content_hash: column.text,
		created_at: column.text,
		updated_at: column.text,
	},
	{ indexes: { by_run: ["run_id", "rank"] } },
);

// ── lecture_sessions (sync-rules: user_lecture_sessions) ──────────────────
const lecture_sessions = new Table(
	{
		user_id: column.text,
		title: column.text,
		duration_sec: column.integer,
		audio_key: column.text,
		audio_mime_type: column.text,
		file_size_bytes: column.integer, // bigint
		transcription: column.text,
		live_transcription_draft: column.text,
		auto_notes: column.text, // jsonb
		auto_notes_markdown: column.text,
		manual_notes: column.text, // jsonb
		manual_note_timestamps: column.text, // jsonb
		speakers: column.text, // jsonb
		saved_material_id: column.text,
		source: column.text, // enum
		status: column.text, // enum
		error_message: column.text,
		error_code: column.text,
		error_retry_count: column.integer,
		applied_disclaimer: column.integer, // bool
		language: column.text,
		transcription_ai_model: column.text,
		transcription_prompt_version: column.text,
		transcription_trace_id: column.text,
		notes_ai_model: column.text,
		notes_prompt_version: column.text,
		notes_trace_id: column.text,
		created_at: column.text,
		updated_at: column.text,
		deleted_at: column.text,
		material_id: column.text,
		active_device_id: column.text,
		locked_at: column.text,
		finalized_at: column.text,
		chunks_total: column.integer,
		chunks_uploaded: column.integer,
		speaker_count: column.integer,
		transcript_id: column.text,
		enable_diarization: column.integer, // bool — миграция 0031
	},
	{ indexes: { by_user: ["user_id", "created_at"] } },
);

// ── recording_segments (sync-rules: user_recording_segments) ──────────────
// Live-транскрипт лекций: реплики STT (transcribeChunkLive / processRecording).
// Read-only на клиенте — пишет только сервер. Читается через usePowerSyncQuery
// на /recordings/new (live-панель) и /recordings/$id (полный транскрипт).
const recording_segments = new Table(
	{
		lecture_session_id: column.text,
		user_id: column.text,
		start_ms: column.integer,
		end_ms: column.integer,
		speaker_id: column.text,
		speaker_label: column.text,
		text: column.text,
		confidence: column.real,
		is_partial: column.integer, // bool
		ordering: column.integer,
		created_at: column.text,
		updated_at: column.text,
	},
	{ indexes: { by_session: ["lecture_session_id", "start_ms"] } },
);

// ── notes (sync-rules: user_notes) ────────────────────────────────────────
// ── recording_doc_blocks (sync-rules: user_recording_doc_blocks) ──────────
// Live-конспект лекций: структурированные блоки markdown с привязкой к
// таймингу (start_ms). Read-only на клиенте — пишет только сервер. Читается
// через usePowerSyncQuery в RecordingDocView (режимы live / review).
const recording_doc_blocks = new Table(
	{
		lecture_session_id: column.text,
		user_id: column.text,
		ordering: column.integer,
		block_type: column.text,
		markdown: column.text,
		start_ms: column.integer,
		end_ms: column.integer,
		is_finalized: column.integer, // bool
		created_at: column.text,
		updated_at: column.text,
	},
	{ indexes: { by_session: ["lecture_session_id", "ordering"] } },
);

// ── recording_manual_notes (sync-rules: user_recording_manual_notes) ──────
// Ручные заметки на таймлайне лекции. Источник правды для панели ManualNotes:
// раньше список жил только в `useState`, и reload во время записи стирал его с
// экрана, хотя строки уже были в БД. Пишется КЛИЕНТОМ локально (лекция —
// офлайн-сценарий), доставка — upload-queue → writeHandler
// modules/recordings/sync.ts.
const recording_manual_notes = new Table(
	{
		lecture_session_id: column.text,
		user_id: column.text,
		at_sec: column.real,
		text: column.text,
		chunk_id: column.text,
		created_at: column.text,
		updated_at: column.text,
		deleted_at: column.text,
	},
	{ indexes: { by_session: ["lecture_session_id", "at_sec"] } },
);

const notes = new Table(
	{
		user_id: column.text,
		material_id: column.text,
		title: column.text,
		content: column.text, // jsonb TipTap document
		content_markdown: column.text,
		detail_level: column.text, // enum
		generated_by_ai: column.integer, // bool
		source_material_id: column.text,
		source_set_id: column.text, // Collections set-wide генерация (миграция 0060)
		source_subsection_id: column.text, // тема курса (миграция 0083)
		// U6 (миграция 0088). `source_message_id` — сообщение репетитора, из
		// которого сохранён конспект; NOT NULL ⇒ конспект read-only (отдельного
		// boolean нет намеренно). Без колонки в клиентской схеме поле молча
		// `undefined`, и весь режим «только для чтения» разваливается офлайн.
		source_message_id: column.text,
		// Материал, созданный действием «Сделать источником» (ключ идемпотентности).
		derived_material_id: column.text,
		ai_model: column.text,
		prompt_version: column.text,
		generation_trace_id: column.text,
		generation_version: column.integer,
		generation_cost: column.real, // numeric
		applied_disclaimer: column.integer, // bool
		created_at: column.text,
		updated_at: column.text,
		deleted_at: column.text,
		plain_text: column.text,
		locale: column.text,
		version: column.integer,
		parent_note_id: column.text,
		// Phase 7 (миграция 0039): статус AI-генерации.
		// 'generating' пока Inngest workflow идёт, 'ready' после persist-note,
		// 'failed' после исчерпания retries. Используется в notes-list для
		// рендера badge «Генерируется…» / «Ошибка».
		generation_status: column.text,
		generation_error_message: column.text,
	},
	{
		indexes: {
			by_user: ["user_id", "updated_at"],
			by_material: ["material_id"],
		},
	},
);

// ── note_versions (sync-rules: user_notes) ────────────────────────────────
const note_versions = new Table(
	{
		note_id: column.text,
		version: column.integer,
		snapshot: column.text, // jsonb
		changed_by: column.text, // enum
		label: column.text,
		created_at: column.text,
		title: column.text,
		content: column.text, // jsonb
		plain_text: column.text,
		reason: column.text, // enum
		ai_model: column.text,
		prompt_version: column.text,
		generation_trace_id: column.text,
		created_by: column.text,
	},
	{ indexes: { by_note: ["note_id", "created_at"] } },
);

// ── note_ai_blocks (sync-rules: user_notes) ───────────────────────────────
const note_ai_blocks = new Table(
	{
		note_id: column.text,
		user_id: column.text,
		position: column.integer,
		type: column.text, // enum
		prompt: column.text,
		selection_text: column.text,
		content: column.text, // jsonb
		plain_text: column.text,
		citations: column.text, // jsonb
		ai_model: column.text,
		prompt_version: column.text,
		generation_trace_id: column.text,
		status: column.text, // enum
		error_message: column.text,
		created_at: column.text,
		updated_at: column.text,
	},
	{
		indexes: {
			by_note: ["note_id", "position"],
			by_user: ["user_id", "updated_at"],
		},
	},
);

// ── glossary_terms (sync-rules: user_glossary_terms) ──────────────────────
const glossary_terms = new Table(
	{
		user_id: column.text,
		material_id: column.text,
		term: column.text,
		definition: column.text,
		language: column.text,
		citations: column.text, // jsonb
		generated_by_ai: column.integer, // bool
		ai_model: column.text,
		prompt_version: column.text,
		generation_trace_id: column.text,
		user_edited: column.integer, // bool
		user_verified: column.integer, // bool
		created_at: column.text,
		updated_at: column.text,
		deleted_at: column.text,
		set_id: column.text,
		subsection_id: column.text, // тема курса (миграция 0083)
		examples: column.text, // jsonb
		occurrences: column.integer,
		importance: column.text, // enum
		cross_references: column.text, // jsonb
		status: column.text, // enum
		sort_order: column.integer,
	},
	{
		indexes: {
			by_material: ["material_id"],
			by_user: ["user_id"],
			by_subsection: ["subsection_id"],
		},
	},
);

// ── flashcard_sets (sync-rules: user_flashcards) ──────────────────────────
const flashcard_sets = new Table(
	{
		user_id: column.text,
		name: column.text,
		description: column.text,
		color: column.text,
		flashcards_count: column.integer,
		generated_from_material_id: column.text,
		source_set_id: column.text, // Collections set-wide генерация (миграция 0060)
		source_subsection_id: column.text, // тема курса (миграция 0083)
		study_progress: column.text, // jsonb
		created_at: column.text,
		updated_at: column.text,
		deleted_at: column.text,
		language: column.text, // varchar
		processing_status: column.text, // enum
		generated_by_ai: column.integer, // bool
		ai_model: column.text,
		prompt_version: column.text,
		generation_trace_id: column.text,
		applied_disclaimer: column.integer, // bool
		due_count: column.integer,
		new_count: column.integer,
	},
	{ indexes: { by_user: ["user_id", "updated_at"] } },
);

// ── flashcards (sync-rules: user_flashcards) ──────────────────────────────
const flashcards = new Table(
	{
		set_id: column.text,
		user_id: column.text,
		sort_order: column.integer,
		type: column.text, // enum
		front_image_url: column.text,
		back_image_url: column.text,
		front: column.text,
		back: column.text,
		hint: column.text,
		options: column.text, // ARRAY → JSON-строка
		correct_answer: column.text,
		audio_url: column.text,
		image_url: column.text,
		marked: column.integer, // bool
		marked_consecutive_correct: column.integer, // smallint
		correct_count: column.integer,
		incorrect_count: column.integer,
		last_studied_at: column.text,
		generated_by_ai: column.integer, // bool
		source_material_id: column.text,
		ai_model: column.text,
		prompt_version: column.text,
		generation_trace_id: column.text,
		generation_cost: column.real, // numeric
		applied_disclaimer: column.integer, // bool
		srs_state: column.text, // enum
		srs_interval: column.real,
		srs_ease_factor: column.real,
		srs_repetition_count: column.integer,
		srs_lapse_count: column.integer,
		srs_due_at: column.text,
		srs_last_reviewed_at: column.text,
		srs_last_rating: column.text, // enum
		content_hash: column.text, // дедуп карточек (миграция 0083)
		tags: column.text, // ARRAY → JSON-строка
		created_at: column.text,
		updated_at: column.text,
		deleted_at: column.text,
		srs_consecutive_again: column.integer,
		srs_algorithm: column.text, // enum
		orphaned: column.integer, // bool
		citation: column.text, // jsonb
		audio_cache_key: column.text,
		back_caption: column.text,
		language: column.text, // varchar
	},
	{ indexes: { by_set: ["set_id"], by_due: ["srs_due_at"] } },
);

// ── flashcard_ratings_queue (sync-rules: user_flashcards) ─────────────────
// Append-only — клиент INSERT'ит, сервер выставляет applied + SRS snapshot.
const flashcard_ratings_queue = new Table(
	{
		flashcard_id: column.text,
		user_id: column.text,
		device_id: column.text,
		rating: column.text, // enum
		client_timestamp: column.text,
		server_received_at: column.text,
		applied: column.integer, // bool
		applied_at: column.text,
		synced_from_offline: column.integer, // bool
		confidence: column.integer,
		duration_ms: column.integer,
		mode: column.text, // enum
		idempotency_id: column.text,
		previous_state: column.text, // enum
		previous_interval: column.integer,
		previous_ease: column.integer,
		new_state: column.text, // enum
		new_interval: column.integer,
		new_ease: column.integer,
	},
	{ indexes: { by_flashcard: ["flashcard_id"], by_user: ["user_id"] } },
);

// ── study_sessions (sync-rules: user_flashcards) ──────────────────────────
const study_sessions = new Table(
	{
		user_id: column.text,
		set_id: column.text,
		started_at: column.text,
		ended_at: column.text,
		cards_studied: column.integer,
		correct_answers: column.integer,
		incorrect_answers: column.integer,
		marked_for_review: column.text, // ARRAY → JSON-строка
		duration_sec: column.integer,
		mode: column.text, // enum
		virtual_set: column.text, // enum
		confidence_data: column.text, // jsonb
		leitner_boxes: column.text, // jsonb
		accuracy_pct: column.integer,
	},
	{ indexes: { by_user: ["user_id", "started_at"] } },
);

// ── tests (sync-rules: user_tests) ────────────────────────────────────────
const tests = new Table(
	{
		user_id: column.text,
		name: column.text,
		description: column.text,
		time_limit: column.integer,
		exam_preset: column.text,
		attempt_count: column.integer,
		last_score: column.integer,
		questions_count: column.integer,
		generated_by_ai: column.integer, // bool
		source_material_id: column.text,
		source_set_id: column.text, // Collections set-wide генерация (миграция 0060)
		source_subsection_id: column.text, // тема курса (миграция 0083)
		ai_model: column.text,
		prompt_version: column.text,
		generation_trace_id: column.text,
		generation_cost: column.real, // numeric
		applied_disclaimer: column.integer, // bool
		generation_status: column.text, // 'generating' | 'ready' | 'failed'
		generation_error_message: column.text,
		is_practice: column.integer, // bool
		created_at: column.text,
		updated_at: column.text,
		deleted_at: column.text,
		passing_score: column.integer,
		language: column.text,
		sort_order: column.integer,
	},
	{ indexes: { by_user: ["user_id", "updated_at"] } },
);

// ── test_questions (sync-rules: user_tests) ───────────────────────────────
const test_questions = new Table(
	{
		test_id: column.text,
		text: column.text,
		question_type: column.text, // enum
		options: column.text, // ARRAY → JSON-строка
		correct: column.text, // ARRAY → JSON-строка
		correct_text: column.text,
		explanation: column.text,
		citations: column.text, // jsonb
		topic: column.text,
		tags: column.text, // ARRAY → JSON-строка
		difficulty: column.text, // enum
		passage_text: column.text,
		sub_questions: column.text, // jsonb
		disputed: column.integer, // bool
		dispute_reason: column.text,
		dispute_count: column.integer,
		hidden: column.integer, // bool
		total_attempts: column.integer,
		correct_attempts: column.integer,
		sort_order: column.integer,
		created_at: column.text,
		updated_at: column.text,
		correct_text_synonyms: column.text, // jsonb
		min_words: column.integer,
	},
	{ indexes: { by_test: ["test_id", "sort_order"] } },
);

// ── attempt_drafts (sync-rules: user_tests) ───────────────────────────────
const attempt_drafts = new Table(
	{
		test_id: column.text,
		user_id: column.text,
		partial_answers: column.text, // jsonb
		partial_confidence_ratings: column.text, // jsonb
		partial_grades: column.text, // jsonb (server-only запись, sync read-only)
		current_question_idx: column.integer,
		started_at: column.text,
		last_saved_at: column.text,
		time_used_sec: column.integer,
		status: column.text,
	},
	{ indexes: { by_user: ["user_id"], by_test: ["test_id"] } },
);

// ── attempt_records (sync-rules: user_tests) ──────────────────────────────
const attempt_records = new Table(
	{
		test_id: column.text,
		user_id: column.text,
		score: column.integer,
		correct_count: column.integer,
		total_count: column.integer,
		time_taken_sec: column.integer,
		answers: column.text, // jsonb
		confidence_ratings: column.text, // jsonb
		created_at: column.text,
		passed: column.integer, // bool
		breakdown_by_topic: column.text, // jsonb
		submitted_at: column.text,
		status: column.text,
	},
	{ indexes: { by_user: ["user_id", "created_at"] } },
);

// ── weak_spots (sync-rules: user_tests) ───────────────────────────────────
const weak_spots = new Table(
	{
		user_id: column.text,
		topic: column.text,
		accuracy: column.real,
		total_questions: column.integer,
		resolved: column.integer, // bool
		resolved_at: column.text,
		last_detected_at: column.text,
		ai_model: column.text,
		prompt_version: column.text,
		generation_trace_id: column.text,
		created_at: column.text,
		material_id: column.text,
		questions_total: column.integer,
		questions_wrong: column.integer,
		confident_wrong: column.integer,
		lucky_guesses: column.integer,
		confidence_mismatch: column.integer, // bool
		source_attempt_id: column.text,
		source_test_id: column.text,
		detection_count: column.integer,
		resolved_by_attempt_id: column.text,
		severity: column.text, // enum
		status: column.text, // enum
	},
	{ indexes: { by_user: ["user_id", "created_at"] } },
);

// ── tutor_sessions (sync-rules: user_tutor_sessions) ──────────────────────
const tutor_sessions = new Table(
	{
		user_id: column.text,
		title: column.text,
		chat_mode: column.text, // enum
		scope: column.text, // jsonb
		scenario_id: column.text,
		skill_ids: column.text, // ARRAY → JSON-строка
		messages_count: column.integer,
		// Курсовой чат (миграция 0084): курс = study_plans.id. NULL — обычный чат.
		plan_id: column.text,
		// «Создать на основе чата» (миграция 0064) — derived-материал на сессию.
		derived_material_id: column.text,
		derived_material_synced_count: column.integer,
		created_at: column.text,
		updated_at: column.text,
		deleted_at: column.text,
		context_selection: column.text, // jsonb
		locale: column.text,
		rolling_summary: column.text,
		summarized_up_to_message_id: column.text,
		last_message_at: column.text,
	},
	{ indexes: { by_user: ["user_id", "updated_at"] } },
);

// ── tutor_messages (sync-rules: user_tutor_messages, auto_subscribe) ─────
const tutor_messages = new Table(
	{
		session_id: column.text,
		user_id: column.text,
		role: column.text, // enum
		content: column.text,
		attachments: column.text, // jsonb
		context_note: column.text,
		citations: column.text, // jsonb
		prompt_tokens: column.integer,
		completion_tokens: column.integer,
		total_cost_minor: column.real, // numeric
		token_count: column.integer,
		applied_disclaimer: column.integer, // bool
		ai_model: column.text,
		prompt_version: column.text,
		generation_trace_id: column.text,
		search_queries: column.text, // jsonb
		created_at: column.text,
		content_parts: column.text, // jsonb
		finish_reason: column.text,
		error_message: column.text,
		latency_ms: column.integer,
		is_hidden_by_edit: column.integer, // bool
		parent_message_id: column.text,
		// Цепочка размышлений (миграция 0085): нарезанные сервером шаги.
		thoughts: column.text, // jsonb → JSON-строка
		thinking_ms: column.integer,
	},
	{ indexes: { by_session: ["session_id", "created_at"] } },
);

// ── tutor_attachments (sync-rules: user_tutor_messages, auto_subscribe) ──
const tutor_attachments = new Table(
	{
		message_id: column.text,
		user_id: column.text,
		type: column.text,
		url: column.text,
		mime_type: column.text,
		file_size_bytes: column.integer,
		filename: column.text,
		duration_ms: column.integer,
		width: column.integer,
		height: column.integer,
		transcription: column.text,
		thumb_hash: column.text,
		created_at: column.text,
	},
	{ indexes: { by_message: ["message_id"], by_user: ["user_id", "created_at"] } },
);

// ── tutor_artifacts (sync-rules: user_tutor_messages, auto_subscribe) ─────
// Артефакты чата курса (миграция 0087): mermaid-диаграммы и растровая
// инфографика. READ-ONLY — writeHandler'а нет намеренно (пишет только сервер,
// удаление через tRPC `tutor.artifacts.delete`). Панель «Артефакты» читает
// эту таблицу локальным UNION-запросом, отдельной tRPC-процедуры `list` нет.
const tutor_artifacts = new Table(
	{
		user_id: column.text,
		session_id: column.text,
		message_id: column.text,
		plan_id: column.text,
		subsection_id: column.text,
		kind: column.text, // 'diagram' | 'infographic'
		subkind: column.text, // тип диаграммы | стиль инфографики
		title: column.text,
		alt_text: column.text,
		source: column.text, // mermaid-исходник (diagram)
		storage_ref: column.text,
		mime: column.text,
		width: column.integer,
		height: column.integer,
		size_bytes: column.integer,
		settings: column.text, // jsonb → JSON-строка в SQLite
		prompt: column.text,
		locale: column.text,
		status: column.text, // 'generating' | 'ready' | 'failed'
		error_message: column.text,
		// AI-провенанс обеих стадий конвейера.
		prompt_model: column.text,
		prompt_trace_id: column.text,
		ai_model: column.text,
		generation_trace_id: column.text,
		prompt_version: column.text,
		created_at: column.text,
		updated_at: column.text,
		deleted_at: column.text,
	},
	{ indexes: { by_plan: ["plan_id", "created_at"], by_session: ["session_id"] } },
);

// ── ai_scenarios (sync-rules: user_tutor_scenarios + tutor_builtin) ───────
// user_id IS NULL = system/builtin сценарии. user_id != NULL = пользовательские.
const ai_scenarios = new Table(
	{
		user_id: column.text,
		title: column.text,
		prompt: column.text,
		category: column.text,
		name: column.text,
		description: column.text,
		system_prompt: column.text,
		locale: column.text,
		is_active: column.integer, // bool
		is_builtin: column.integer, // bool
		deleted_at: column.text,
		created_at: column.text,
		updated_at: column.text,
	},
	{
		indexes: {
			by_user_active: ["user_id", "is_active"],
			by_builtin_locale: ["is_builtin", "locale"],
		},
	},
);

// ── ai_skills (sync-rules: user_tutor_scenarios + tutor_builtin) ──────────
const ai_skills = new Table(
	{
		user_id: column.text,
		label: column.text,
		instruction: column.text,
		name: column.text,
		description: column.text,
		locale: column.text,
		is_builtin: column.integer, // bool
		deleted_at: column.text,
		created_at: column.text,
		updated_at: column.text,
	},
	{
		indexes: {
			by_builtin_locale: ["is_builtin", "locale"],
		},
	},
);

// ── essay_submissions (sync-rules: user_essays) ───────────────────────────
const essay_submissions = new Table(
	{
		user_id: column.text,
		title: column.text,
		essay_text: column.text,
		essay_file_url: column.text,
		word_count: column.integer,
		char_count: column.integer,
		language: column.text,
		feedback_language: column.text,
		topic: column.text,
		academic_level: column.text, // enum
		format: column.text, // enum
		status: column.text, // enum
		grading_result_id: column.text,
		applied_disclaimer: column.integer, // bool
		created_at: column.text,
		updated_at: column.text,
		deleted_at: column.text,
		content: column.text, // jsonb
		essay_file_s3_key: column.text,
		essay_file_name: column.text,
		attempt: column.integer,
		cancellation_reason: column.text,
		queued_at: column.text,
		graded_at: column.text,
		error_message: column.text,
	},
	{ indexes: { by_user: ["user_id", "updated_at"] } },
);

// ── grading_results (sync-rules: user_essays) ─────────────────────────────
// Read-only клиенту: server-AI-generated, нет writeHandler (см. modules/essays/).
// Зеркало Drizzle schema (apps/server/src/shared/db/schema/essays.ts → gradingResults).
const grading_results = new Table(
	{
		user_id: column.text,
		submission_id: column.text,
		attempt: column.integer,
		overall_score: column.integer,
		overall_grade: column.text,
		overall_feedback: column.text,
		criteria: column.text, // jsonb → JSON-строка
		annotations: column.text, // jsonb → JSON-строка
		recommendations: column.text, // text[] → JSON-строка
		strengths: column.text, // text[] → JSON-строка
		improvements: column.text, // text[] → JSON-строка
		ai_detection_score: column.integer,
		report_url: column.text,
		ai_model: column.text,
		prompt_version: column.text,
		generation_trace_id: column.text,
		generation_cost_micros: column.integer,
		input_tokens: column.integer,
		output_tokens: column.integer,
		duration_ms: column.integer,
		created_at: column.text,
	},
	{ indexes: { by_submission: ["submission_id", "created_at"] } },
);

// ── visual_analyses (sync-rules: user_visual) ─────────────────────────────
const visual_analyses = new Table(
	{
		user_id: column.text,
		image_url: column.text,
		source_material_id: column.text,
		crop_region: column.text, // jsonb
		analysis_markdown: column.text,
		detected_language: column.text,
		detected_content_types: column.text, // ARRAY → JSON-строка
		saved_material_id: column.text,
		status: column.text, // enum
		failure_reason: column.text,
		applied_disclaimer: column.integer, // bool
		ai_model: column.text,
		prompt_version: column.text,
		generation_trace_id: column.text,
		generation_cost_minor: column.integer,
		created_at: column.text,
		updated_at: column.text,
		deleted_at: column.text,
		title: column.text,
		image_s3_key: column.text,
		mime: column.text,
		width: column.integer,
		height: column.integer,
		size_bytes: column.integer,
		user_hint: column.text,
		language: column.text,
		category: column.text,
		ocr_text: column.text,
		key_points: column.text, // ARRAY → JSON-строка
		category_data: column.text, // jsonb
		source_context: column.text, // jsonb
		error_message: column.text,
	},
	{ indexes: { by_user: ["user_id", "created_at"] } },
);

// ── visual_follow_ups (sync-rules: user_visual) ───────────────────────────
const visual_follow_ups = new Table(
	{
		analysis_id: column.text,
		question: column.text,
		answer: column.text,
		ai_model: column.text,
		prompt_version: column.text,
		generation_trace_id: column.text,
		generation_cost_minor: column.integer,
		created_at: column.text,
	},
	{ indexes: { by_analysis: ["analysis_id", "created_at"] } },
);

// ── audio_recaps (sync-rules: user_audio_recaps) ──────────────────────────
const audio_recaps = new Table(
	{
		user_id: column.text,
		title: column.text,
		topic: column.text,
		material_ids: column.text, // ARRAY → JSON-строка
		recap_format: column.text, // enum
		voice_mode: column.text, // enum
		voice_a: column.text,
		voice_b: column.text,
		target_duration_min: column.integer,
		language: column.text,
		audio_key: column.text,
		script_text: column.text,
		duration_sec: column.integer,
		status: column.text, // enum
		share_token: column.text,
		share_link_revoked_at: column.text,
		ai_model: column.text,
		script_prompt_version: column.text,
		tts_model: column.text,
		generation_trace_id: column.text,
		generation_cost: column.real, // numeric
		applied_disclaimer: column.integer, // bool
		created_at: column.text,
		updated_at: column.text,
		deleted_at: column.text,
		material_id: column.text,
		source_note_id: column.text,
		// Тема курса (миграция 0083). ВНИМАНИЕ: user_audio_recaps — единственный
		// стрим с ЯВНЫМ списком колонок; колонка дописана и туда.
		source_subsection_id: column.text,
		voices_json: column.text, // jsonb
		script_segments: column.text, // jsonb
		target_word_count: column.integer,
		waveform_key: column.text,
		file_size_bytes: column.integer, // bigint
		failure_reason: column.text,
		partial_segments_ready: column.integer,
		cost_estimate_cents: column.integer,
		cost_actual_cents: column.integer,
		dedup_key: column.text,
		bitrate: column.integer,
		sample_rate: column.integer,
		audio_format: column.text,
		ready_at: column.text,
		preview_key: column.text, // миграция 0035
		persona_ids: column.text, // text[] → JSON-строка, миграция 0036
		style_preset: column.text, // миграция 0038 (AUDIO-RECAP-PRESETS-AND-UPLOAD)
		custom_brief: column.text, // миграция 0038
	},
	{
		indexes: {
			by_user: ["user_id", "created_at"],
			by_material: ["material_id"],
			by_status: ["status"],
		},
	},
);

// ── audio_recap_playback_log (sync-rules: user_audio_recaps) ──────────────
const audio_recap_playback_log = new Table(
	{
		audio_recap_id: column.text,
		user_id: column.text,
		session_id: column.text,
		position_sec: column.integer,
		duration_listened_sec: column.integer,
		playback_speed: column.text,
		completed_at: column.text,
		platform: column.text,
		created_at: column.text,
		updated_at: column.text,
	},
	{
		indexes: {
			by_recap: ["audio_recap_id", "user_id"],
			by_user: ["user_id", "updated_at"],
		},
	},
);

// ── user_activity_log (sync-rules: user_activity) ─────────────────────────
const user_activity_log = new Table(
	{
		user_id: column.text,
		activity_date: column.text, // date
		activity_type: column.text,
		count: column.integer,
		minutes: column.integer,
		timezone: column.text,
		last_occurred_at: column.text,
		created_at: column.text,
		updated_at: column.text,
	},
	{ indexes: { by_user: ["user_id", "activity_date"] } },
);

// ── streak_state (sync-rules: user_activity) ──────────────────────────────
const streak_state = new Table({
	user_id: column.text,
	current_streak: column.integer,
	longest_streak: column.integer,
	last_activity_date: column.text, // date
	weekly_freeze_used: column.integer, // bool
	total_active_days: column.integer,
	updated_at: column.text,
	last_finalized_date: column.text, // date
	freeze_balance: column.integer,
	freezes_reset_at: column.text,
	auto_freeze_enabled: column.integer, // bool
	daily_goal_minutes: column.integer,
	nudge_hour_local: column.integer,
	last_nudge_at: column.text,
	total_study_minutes: column.integer,
	created_at: column.text,
});

// ── study_plans (sync-rules: user_study_plan) ─────────────────────────────
const study_plans = new Table(
	{
		user_id: column.text,
		name: column.text,
		exam_date: column.text, // date
		status: column.text, // enum
		generated_from_set_id: column.text,
		generated_from_template_id: column.text,
		total_sections: column.integer,
		completed_sections: column.integer,
		created_at: column.text,
		updated_at: column.text,
		title: column.text,
		description: column.text,
		exam_subject: column.text,
		country: column.text,
		education_subsystem: column.text,
		template_id: column.text,
		template_slug: column.text,
		source_type: column.text, // enum
		progress_pct: column.integer,
		total_subsections: column.integer,
		completed_subsections: column.integer,
		ai_model: column.text,
		prompt_version: column.text,
		generation_trace_id: column.text,
		locale: column.text,
		deleted_at: column.text,
		// ── Учебный календарь (миграция 0068): настройки нагрузки ──
		schedule_days: column.text, // jsonb number[] → JSON-строка
		daily_minutes_cap: column.integer,
		schedule_start_date: column.text, // timestamptz → ISO-строка
		// ── Обучающая система (миграция 0083): курс == план ──
		is_default: column.integer, // bool — «Мой курс»
		program_built_at: column.text, // timestamptz → ISO-строка
		// Черновик программы (миграция 0132): построена, но не утверждена.
		// Синкается, потому что бейдж «Черновик» и панель утверждения обязаны
		// быть верными офлайн — иначе курс, утверждённый на телефоне, показал бы
		// на десктопе кнопку «Утвердить» до первого онлайн-запроса.
		program_confirmed_at: column.text, // timestamptz → ISO-строка
		audio_streak: column.integer,
		preferred_recap_format: column.text, // 'summary' | 'lecture' | 'podcast'
		// ── Обложка курса (миграция 0114) ──
		// Синкаем ровно то, что рисует карточка: адрес, blur-up хэш и статус.
		// `cover_storage_key` / `cover_prompt` серверные — на клиенте им делать
		// нечего, а лишняя колонка это лишний трафик на каждом апдейте плана.
		cover_url: column.text,
		cover_thumb_hash: column.text,
		cover_status: column.text, // 'pending' | 'ready' | 'failed'
		// ── Курс на языке ученика (миграция 0116) ──
		// jsonb приезжает JSON-строкой; читатель разбирает её одним хелпером
		// `pickCourseLocalized`. Без этих колонок офлайн показывал бы оригинал,
		// хотя ученик уже перевёл курс — расхождение онлайна и офлайна.
		display_locale: column.text,
		title_localized: column.text,
		description_localized: column.text,
		translation_status: column.text,
		// ── Цель курса и входная диагностика (миграция 0128) ──
		// Цель читает шапка курса (её можно править из UI), поэтому она обязана
		// быть офлайн — иначе поле цели показывало бы пустоту при живой цели.
		goal_target: column.text,
		goal_focus: column.text,
		goal_level: column.text, // 'beginner' | 'intermediate' | 'advanced'
		goal_depth: column.text, // 'quick' | 'standard' | 'deep'
		// Возрастная группа слушателя (миграция 0139): '3-7' | '7-11' | '11-14' | '14-17'.
		// NULL — не заявляли, подача взрослая (НЕ синоним старшей группы).
		goal_audience_age: column.text,
		// Диагностику рисует баннер каркаса: без этих колонок он вылезал бы
		// офлайн даже у того, кто тест уже прошёл.
		placement_test_id: column.text,
		placement_status: column.text, // 'skipped' | 'done'; NULL — ещё не решал
		// Просьба из брифа «не знаю уровень — проверьте тестом» (миграция 0132).
		// Отдельно от статуса выше: там решение по уже предложенной диагностике,
		// здесь — просьба, заявленная до сборки программы.
		placement_requested: column.integer, // bool
	},
	{ indexes: { by_user: ["user_id", "updated_at"] } },
);

// ── study_sections (sync-rules: user_study_plan) ──────────────────────────
const study_sections = new Table(
	{
		plan_id: column.text,
		title: column.text,
		order: column.integer,
		status: column.text, // enum
		estimated_minutes: column.integer,
		progress_percent: column.integer,
		description: column.text,
		// Переводы каркаса (миграция 0116) — jsonb приезжает JSON-строкой.
		title_localized: column.text,
		description_localized: column.text,
		sort_order: column.integer,
		estimated_hours: column.integer,
		progress_pct: column.integer,
		completed: column.integer, // bool
		completed_at: column.text,
		total_subsections: column.integer,
		completed_subsections: column.integer,
		created_at: column.text,
		updated_at: column.text,
	},
	{ indexes: { by_plan: ["plan_id", "sort_order"] } },
);

// ── study_subsections (sync-rules: user_study_plan) ───────────────────────
const study_subsections = new Table(
	{
		section_id: column.text,
		title: column.text,
		order: column.integer,
		status: column.text, // enum
		progress_percent: column.integer,
		recommended_activities: column.text, // jsonb
		plan_id: column.text,
		description: column.text,
		// Переводы каркаса (миграция 0116) — jsonb приезжает JSON-строкой.
		title_localized: column.text,
		description_localized: column.text,
		sort_order: column.integer,
		estimated_minutes: column.integer,
		progress_pct: column.integer,
		completed: column.integer, // bool
		completed_at: column.text,
		// Обучающая система (миграция 0083): тему закрыли ЯВНО (кнопка «Завершить
		// тему»), пересчёт прогресса не имеет права это сбросить.
		completed_explicitly: column.integer, // bool
		total_activities: column.integer,
		completed_activities: column.integer,
		linked_materials_count: column.integer,
		// ── Замер владения темой (миграция 0128) ──
		// Это НЕ `progress_pct` выше: тот считает закрытые активности, этот —
		// насколько тема освоена по последней попытке теста. Метки «завершена с
		// пробелами» / «уже знаете» на маршруте тем рисуются отсюда, поэтому
		// колонки нужны офлайн вместе с самим маршрутом.
		assessed_mastery: column.integer, // 0..100
		assessment_source: column.text, // 'placement' | 'quiz' | 'checkpoint'
		assessed_at: column.text, // timestamptz → ISO-строка
		created_at: column.text,
		updated_at: column.text,
	},
	{ indexes: { by_section: ["section_id", "sort_order"] } },
);

// ── study_subsection_materials (sync-rules: user_study_plan) ──────────────
const study_subsection_materials = new Table(
	{
		subsection_id: column.text,
		// denorm ради стрима (миграция 0140): фильтр идёт по плану, а не по теме.
		// Колонки нет в клиентской схеме — строка приедет без неё, и локальные
		// выборки по плану молча вернут пусто.
		plan_id: column.text,
		material_id: column.text,
		chunk_id: column.text,
		similarity: column.real,
		auto_linked: column.integer, // bool
		created_at: column.text,
		similarity_decimal: column.real, // numeric
	},
	{ indexes: { by_subsection: ["subsection_id"], by_plan: ["plan_id"] } },
);

// ── study_subsection_activities (sync-rules: user_study_plan) ─────────────
// Чек-лист активностей подраздела. Своей колонки user_id нет — стрим фильтрует
// по денормализованному plan_id.
// Обучающая система (0083, батч 2): для таблицы появляется writeHandler (PATCH
// {status, completedAt}) — офлайн-отметка активности темы. Ownership — через
// JOIN study_subsections → study_sections → study_plans.user_id.
const study_subsection_activities = new Table(
	{
		subsection_id: column.text,
		section_id: column.text,
		plan_id: column.text,
		sort_order: column.integer,
		type: column.text, // enum study_activity_type
		reference_id: column.text,
		metadata: column.text, // jsonb
		status: column.text, // enum study_activity_status
		completed_at: column.text,
		expected_duration_minutes: column.integer,
		created_at: column.text,
		updated_at: column.text,
	},
	{ indexes: { by_subsection: ["subsection_id"], by_plan: ["plan_id"] } },
);

// ── daily_progress (sync-rules: user_progress) ────────────────────────────
// Server-canonical дневные счётчики. Экран прогресса считает из них KPI
// локально, поэтому офлайн показывает реальные данные, а не онбординг.
const daily_progress = new Table(
	{
		user_id: column.text,
		date: column.text,
		flashcards_reviewed: column.integer,
		flashcards_correct: column.integer,
		quiz_attempts: column.integer,
		quiz_score_avg: column.integer,
		materials_viewed: column.integer,
		materials_created: column.integer,
		notes_created: column.integer,
		notes_edited: column.integer,
		notes_viewed: column.integer,
		visual_viewed: column.integer,
		audio_recaps_listened: column.integer,
		voice_tutor_minutes: column.integer,
		recordings_minutes: column.integer,
		tutor_messages: column.integer,
		total_minutes: column.integer,
		mastery_avg: column.integer,
		created_at: column.text,
		updated_at: column.text,
	},
	{ indexes: { by_user_date: ["user_id", "date"] } },
);

// ── mastery_by_topic (sync-rules: user_progress) ──────────────────────────
const mastery_by_topic = new Table(
	{
		user_id: column.text,
		topic: column.text,
		material_id: column.text,
		mastery_score: column.integer,
		total_reviews: column.integer,
		total_correct: column.integer,
		weak_spot_resolved: column.integer, // bool
		last_reviewed_at: column.text,
		last_computed_at: column.text,
		created_at: column.text,
		updated_at: column.text,
	},
	{ indexes: { by_user: ["user_id"] } },
);

// ── recommendations (sync-rules: user_progress) ───────────────────────────
// Пул рекомендаций с TTL. Офлайн показывает снимок на момент последнего синка.
const recommendations = new Table(
	{
		user_id: column.text,
		generated_at: column.text,
		expires_at: column.text,
		items: column.text, // jsonb RecommendationItem[]
		brief_cards: column.text, // jsonb BriefCard[]
		brief_dirty_signal: column.text,
		dismissed_item_ids: column.text, // jsonb string[]
		acted_item_ids: column.text, // jsonb string[]
		// Отложенные не скрываются, а переставляются в конец колоды — без этой
		// колонки офлайн-порядок расходился бы с серверным.
		postponed_item_ids: column.text, // jsonb string[]
	},
	{ indexes: { by_user: ["user_id"] } },
);

// ── essay_annotations (sync-rules: user_essay_feedback) ───────────────────
// Разметка проверенного эссе: цитата + комментарий + предложенная правка.
const essay_annotations = new Table(
	{
		essay_id: column.text,
		grading_result_id: column.text,
		user_id: column.text,
		kind: column.text, // enum
		severity: column.text, // enum
		start_offset: column.integer,
		end_offset: column.integer,
		quote: column.text,
		comment: column.text,
		suggested_rewrite: column.text,
		criterion_key: column.text,
		created_by: column.text,
		resolved: column.integer, // bool
		resolved_at: column.text,
		user_override_comment: column.text,
		created_at: column.text,
	},
	{ indexes: { by_essay: ["essay_id"], by_grading: ["grading_result_id"] } },
);

// ── essay_reports (sync-rules: user_essay_feedback) ───────────────────────
// Только метаданные PDF — сам файл лежит в S3 и офлайн недоступен.
const essay_reports = new Table(
	{
		essay_id: column.text,
		grading_result_id: column.text,
		user_id: column.text,
		pdf_s3_key: column.text,
		size_bytes: column.integer,
		page_count: column.integer,
		signed_url_expires_at: column.text,
		created_at: column.text,
	},
	{ indexes: { by_essay: ["essay_id"] } },
);

// ── user_study_load_settings (sync-rules: user_study_load) ────────────────
// Глобальные (per-user, 1:1) настройки учебной нагрузки (PLANNING-REWORK,
// миграция 0070). PK = user_id (НЕ id — implicit-PK PowerSync = поле `id`,
// но строки уникальны по user_id; пишутся через writeHandler upsert).
// schedule_days — jsonb number[] → JSON-строка. daily_minutes_cap — minutes/day.
const user_study_load_settings = new Table(
	{
		user_id: column.text,
		daily_minutes_cap: column.integer,
		schedule_days: column.text, // jsonb number[] → JSON-строка
		created_at: column.text,
		updated_at: column.text,
	},
	{ indexes: { by_user: ["user_id"] } },
);

// ── calendar_events (sync-rules: user_calendar) ───────────────────────────
const calendar_events = new Table(
	{
		user_id: column.text,
		type: column.text, // enum
		title: column.text,
		start_at: column.text,
		end_at: column.text,
		study_plan_id: column.text,
		subsection_id: column.text,
		reminded: column.integer, // bool
		created_at: column.text,
		updated_at: column.text,
		description: column.text,
		event_type: column.text,
		all_day: column.integer, // bool
		recurrence_rule: column.text,
		recurrence_exceptions: column.text, // jsonb
		reminder_minutes: column.integer,
		plan_id: column.text,
		color: column.text,
		location: column.text,
		metadata: column.text, // jsonb
		exam_date_flag: column.integer, // bool
		deleted_at: column.text,
		// Учебный календарь (миграция 0068): флаг ручной перестановки урока (drag).
		user_overridden: column.integer, // bool
	},
	{ indexes: { by_user_start: ["user_id", "start_at"] } },
);

// ── voice_sessions (sync-rules: user_voice_sessions) — NO PERSISTENCE OF AUDIO
const voice_sessions = new Table(
	{
		user_id: column.text,
		topic: column.text,
		material_ids: column.text, // ARRAY → JSON-строка
		status: column.text,
		started_at: column.text,
		ended_at: column.text,
		duration_sec: column.integer,
		lesson_plan_id: column.text,
		transcripts: column.text, // jsonb (опц. при saveTranscriptOptIn)
		ai_model: column.text,
		prompt_version: column.text,
		generation_trace_id: column.text,
		created_at: column.text,
		material_id: column.text,
		set_id: column.text,
		language: column.text,
		quota_minutes_consumed: column.integer,
		transcript_summary: column.text,
		save_transcript_opt_in: column.integer, // bool
		voice_id: column.text,
		speech_speed: column.real, // numeric
		applied_disclaimer: column.integer, // bool
		reconnect_count: column.integer,
		cost_estimate_minor: column.integer,
		cost_actual_minor: column.integer,
		audio_retention_mode: column.text,
		live_session_trace_id: column.text,
		error_message: column.text,
		deleted_at: column.text,
		updated_at: column.text,
		current_section_index: column.integer, // миграция 0033
		completed_section_ids: column.text, // jsonb — миграция 0033
	},
	{ indexes: { by_user_status: ["user_id", "status", "started_at"] } },
);

// ── voice_lesson_plans (sync-rules: user_voice_lesson_plans) ──────────────
const voice_lesson_plans = new Table(
	{
		user_id: column.text,
		voice_session_id: column.text,
		material_id: column.text,
		set_id: column.text,
		topic: column.text,
		language: column.text,
		goals: column.text, // jsonb
		activities: column.text, // jsonb
		estimated_duration_sec: column.integer,
		// Подача занятия (миграция 0143): глубина и возрастная группа слушателя.
		level: column.text,
		audience_age: column.text,
		edited_by_user: column.integer, // bool
		ai_model: column.text,
		prompt_version: column.text,
		generation_trace_id: column.text,
		created_at: column.text,
		updated_at: column.text,
	},
	{ indexes: { by_user_created: ["user_id", "created_at"] } },
);

// ── character_sessions (sync-rules: user_character_sessions) — Ш7, федерация
// с CharacterHub. ТОЛЬКО метаданные: сообщений в StudyHub нет by design (§3
// федерации), офлайн виден список сессий, а сам чат просит сеть.
const character_sessions = new Table(
	{
		user_id: column.text,
		external_session_id: column.text,
		character_external_id: column.text,
		character_title: column.text,
		character_avatar_url: column.text,
		scenario: column.text,
		locale: column.text,
		material_ids: column.text, // jsonb → JSON-строка
		subsection_id: column.text,
		message_count: column.integer,
		last_message_at: column.text,
		created_at: column.text,
		updated_at: column.text,
	},
	{ indexes: { by_user_created: ["user_id", "created_at"] } },
);

export const AppSchema = new Schema({
	users,
	user_stats,
	quotas,
	notifications,
	notification_preferences,
	folders,
	set_items,
	materials,
	source_discovery_runs,
	source_discovery_candidates,
	lecture_sessions,
	recording_segments,
	recording_doc_blocks,
	recording_manual_notes,
	notes,
	note_versions,
	note_ai_blocks,
	glossary_terms,
	flashcard_sets,
	flashcards,
	flashcard_ratings_queue,
	study_sessions,
	tests,
	test_questions,
	attempt_drafts,
	attempt_records,
	weak_spots,
	tutor_sessions,
	tutor_messages,
	tutor_attachments,
	tutor_artifacts,
	ai_scenarios,
	ai_skills,
	essay_submissions,
	grading_results,
	visual_analyses,
	visual_follow_ups,
	audio_recaps,
	audio_recap_playback_log,
	user_activity_log,
	streak_state,
	study_plans,
	study_sections,
	study_subsections,
	study_subsection_materials,
	study_subsection_activities,
	daily_progress,
	mastery_by_topic,
	recommendations,
	essay_annotations,
	essay_reports,
	user_study_load_settings,
	calendar_events,
	voice_sessions,
	voice_lesson_plans,
	character_sessions,
});

export type Database = (typeof AppSchema)["types"];
